import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@contexts/LanguageContext';
import { StatusBadge } from '@components/common/StatusBadge/StatusBadge';
import Icon from '@components/Icon/Icon';
import { faUserCircle } from '@fortawesome/free-solid-svg-icons';
import ResourceActions from '../ResourceActions';
import './ResourceInfo.scss';

export const ResourceInfo = ({
    resource,
    user,
    resourcePermissions,
    onEdit,
    onDelete,
    className = ''
}) => {
    const { t } = useLanguage();
    const navigate = useNavigate();

    if (!resource) {
        return null;
    }

    // Simplified availability info calculation for details display
    const getAvailabilityDetails = (resource) => {
        if (!resource) return null;

        if (resource.consumable) {
            const currentQuantity = resource.current_quantity ?? resource.quantity ?? 0;
            if (currentQuantity > 0) {
                const unit = resource.resource_metadata?.find(meta => meta.metadata_key === 'unit')?.value;
                return {
                    text: t('resourceView.available'),
                    className: 'status-available',
                    details: `${currentQuantity} ${unit ? `${unit} ${t('resourceView.available')}` : t('resourceView.units_available')}`
                };
            } else {
                return {
                    text: t('resourceView.outOfStock'),
                    className: 'status-unavailable',
                    details: t('resourceView.needs_refill')
                };
            }
        } else {
            if (resource.is_currently_borrowed) {
                const borrowerName = resource.current_borrower?.name || resource.current_borrower?.email || t('resourceView.unknown_user');
                return {
                    text: t('resourceView.borrowed'),
                    className: 'status-borrowed',
                    details: `${t('resourceView.borrowed_by')}: ${borrowerName}`
                };
            } else {
                return {
                    text: t('resourceView.available'),
                    className: 'status-available',
                    details: t('resourceView.ready_to_borrow')
                };
            }
        }
    };

    const availabilityDetails = getAvailabilityDetails(resource);

    const handleCategoryClick = () => {
        if (resource.category?.id) {
            navigate(`/categories/${resource.category.id}`);
        }
    };

    const handleLocationClick = () => {
        if (resource.location?.id) {
            navigate(`/locations/${resource.location.id}`);
        }
    };

    return (
        <div className={`resource-info ${className}`}>
            <div className="resource-header">
                <h1 className="resource-title">{resource.name}</h1>
                <StatusBadge
                    model={resource}
                    type="borrowing"
                    className="resource-status-badge"
                />
            </div>

            {availabilityDetails.details && (
                <div className={`availability-details ${availabilityDetails.className}`}>
                    {availabilityDetails.details}
                </div>
            )}

            <ResourceActions
                resource={resource}
                user={user}
                resourcePermissions={resourcePermissions}
                onEdit={onEdit}
                onDelete={onDelete}
            />

            <div className="resource-details">
                <div className="details-grid">
                    {resource.category && (
                        <div className="detail-item">
                            <div className="detail-label">{t('resourceView.category')}</div>
                            <div className="detail-value category-value">
                                <span
                                    className="category-badge clickable"
                                    style={resource.category.color ? { backgroundColor: resource.category.color } : {}}
                                    onClick={handleCategoryClick}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleCategoryClick();
                                        }
                                    }}
                                    title={t('common.view_category', 'View category details')}
                                >
                                    {resource.category.name}
                                </span>
                            </div>
                        </div>
                    )}

                    {resource.personal && resource.owner && (
                        <div className="detail-item">
                            <div className="detail-label">{t('resourceView.owner')}</div>
                            <div className="detail-value">
                                <span className="owner-info">
                                    <Icon icon={faUserCircle} className="owner-icon" />
                                    <span className="owner-name">{resource.owner.name || resource.owner.email}</span>
                                </span>
                                <small className="personal-label">{t('resourceView.personalResource')}</small>
                            </div>
                        </div>
                    )}

                    {!resource.personal && resource.location && (
                        <div className="detail-item">
                            <div className="detail-label">{t('resourceView.location')}</div>
                            <div className="detail-value">
                                <span
                                    className="location-badge clickable"
                                    onClick={handleLocationClick}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleLocationClick();
                                        }
                                    }}
                                    title={t('common.view_location', 'View location details')}
                                >
                                    <Icon icon="map-marker-alt" className="location-icon" />
                                    <span className="location-name">{resource.location.name}</span>
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};export default ResourceInfo;
